import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { scheduleApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { Alert, Button, LoadingBlock, TextInput } from '../components/ui.tsx';
import { WorkEmpty, WorkPage } from '../components/work-page.tsx';
import { AvailabilityPicker } from '../components/schedule/availability-picker.tsx';
import { formatDuration } from '../components/schedule/format.ts';
import {
  acceptProposedTime,
  ExplainedRefusal,
  proposalHasPassed,
  proposedTimeLabel,
  RequestAlreadyHandled,
} from '../components/schedule/change-request.ts';
import { formatLocalTime, formatShortDate, formatShortInstant } from '../i18n/datetime.ts';
import type { Appointment } from '@shared/types/index.ts';

/**
 * Schedule › Change requests: every visit still ahead whose customer asked for
 * a different time, each with its answer one click away — move it to the time
 * they picked, keep it as booked, or choose another time. The dashboard's
 * "needs attention" line opens here.
 *
 * Read live every time (see `scheduleApi.changeRequests`). An answered request
 * leaves the list, so what happened is said at the top of the page rather
 * than on a card that is about to disappear.
 */
export default function ChangeRequestsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('changeRequests.documentTitle'));
  const { can, currentLocation, locations } = useSession();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);
  const canAnswer = can('schedule:write');
  // Like the schedule's payment filter: the dashboard's count can cover every
  // location, while this list (like every schedule read) covers the session's
  // own. `scope=all` on arrival says so rather than looking shorter than the
  // number that led here.
  const fromAllLocationsScope = searchParams.get('scope') === 'all' && locations.length > 1;

  const requests = useQuery({
    queryKey: ['change-requests', currentLocation?.id],
    queryFn: () => scheduleApi.changeRequests(),
    networkMode: 'always',
    staleTime: 0,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['change-requests'] });
    // The same visits sit on the calendar and behind the dashboard's count.
    void queryClient.invalidateQueries({ queryKey: ['appointments'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const items = requests.data?.items ?? [];

  return (
    <WorkPage title={t('changeRequests.title')} description={t('changeRequests.description')}>
      <div aria-live="polite">
        {notice ? (
          <div className="mb-4">
            <Alert tone={notice.tone}>{notice.text}</Alert>
          </div>
        ) : null}
      </div>

      {fromAllLocationsScope ? (
        <div className="mb-4">
          <Alert>
            {t('changeRequests.locationNote', {
              location: currentLocation?.name ?? t('changeRequests.locationFallback'),
            })}
          </Alert>
        </div>
      ) : null}

      {!canAnswer ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('changeRequests.readOnly')}</p>
      ) : null}

      {requests.isPending ? <LoadingBlock label={t('changeRequests.loading')} /> : null}

      {requests.isError ? (
        <Alert tone="error" title={t('changeRequests.loadError')}>
          <Button className="mt-2" onClick={() => void requests.refetch()}>
            {t('changeRequests.tryAgain')}
          </Button>
        </Alert>
      ) : null}

      {requests.isSuccess && items.length === 0 ? (
        <WorkEmpty title={t('changeRequests.emptyTitle')}>
          <p>{t('changeRequests.emptyBody')}</p>
        </WorkEmpty>
      ) : null}

      {requests.data?.truncated ? (
        <div className="mb-4">
          <Alert tone="warning">{t('changeRequests.truncated')}</Alert>
        </div>
      ) : null}

      {items.length > 0 ? (
        <div className="space-y-4">
          {items.map((appointment) => (
            <ChangeRequestCard
              // Keyed on the request too: a customer who asks again is a new
              // question, and the card's half-filled answer belonged to the old one.
              key={`${appointment.id}:${appointment.changeRequestedAt ?? ''}`}
              appointment={appointment}
              canAnswer={canAnswer}
              onAnswered={() => {
                setNotice({
                  tone: 'success',
                  text: t('changeRequests.answered', { customer: appointment.customerName }),
                });
                refresh();
              }}
              onStale={(message) => {
                setNotice({ tone: 'warning', text: message });
                refresh();
              }}
            />
          ))}
        </div>
      ) : null}
    </WorkPage>
  );
}

function ChangeRequestCard({
  appointment,
  canAnswer,
  onAnswered,
  onStale,
}: {
  appointment: Appointment;
  canAnswer: boolean;
  onAnswered: () => void;
  /** The request moved on before the click; the list is out of date. */
  onStale: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [moveDate, setMoveDate] = useState(appointment.scheduledDate);
  const [moveTime, setMoveTime] = useState(appointment.startTimeLocal);

  const proposed = proposedTimeLabel(appointment);
  // Asked again at the click by `acceptProposedTime`: a page left open across
  // the deadline never re-renders.
  const proposedHasPassed = proposalHasPassed(appointment);
  const booked = `${formatShortDate(appointment.scheduledDate)} ${formatLocalTime(appointment.startTimeLocal)}`;
  const headingId = `change-request-${appointment.id}`;

  const showError = (caught: unknown) =>
    setError(
      caught instanceof ApiRequestError || caught instanceof ExplainedRefusal
        ? caught.message
        : t('appointmentActions.updateError'),
    );
  const onSuccess = () => {
    setError(null);
    onAnswered();
  };
  /**
   * After a refusal: is this card's request still the one waiting?
   *
   * A 409 is either a slot that is taken or a request that is gone, and the
   * visit itself says which. Gone is a stamp that moved on (answered, or asked
   * again) or a visit no longer waiting at all — completing or canceling it
   * leaves the stamp behind, so the status has to be asked too.
   */
  const reportRefusal = async (caught: unknown) => {
    if (caught instanceof RequestAlreadyHandled) return onStale(caught.message);
    if (caught instanceof ApiRequestError && caught.status === 409) {
      const current = await scheduleApi.appointment(appointment.id).catch(() => null);
      if (
        current &&
        (current.status !== 'change_requested' || current.changeRequestedAt !== appointment.changeRequestedAt)
      )
        return onStale(caught.message);
    }
    showError(caught);
  };

  const accept = useMutation({
    mutationFn: () =>
      acceptProposedTime(
        appointment,
        {
          passed: t('appointmentActions.requestedTimeHasPassedShort'),
          handled: t('appointmentActions.requestAlreadyHandled'),
        },
        // Reported from `onError` below, once, with the sentence.
        () => undefined,
      ),
    onSuccess,
    // The browser's own re-read catches most races; one landing between it
    // and the guarded write comes back as the server's 409.
    onError: reportRefusal,
  });
  /**
   * "No" to the customer's request: the visit stays where it is, confirmed.
   * Sent with the request it answers, so one a colleague has already dealt
   * with — or one the customer has replaced — is refused rather than cleared.
   */
  const keep = useMutation({
    mutationFn: () =>
      scheduleApi.confirm(appointment.id, { expectedChangeRequestedAt: appointment.changeRequestedAt ?? undefined }),
    onSuccess,
    // A conflict here means there is no longer this request to answer.
    onError: (caught: unknown) =>
      caught instanceof ApiRequestError && caught.status === 409 ? onStale(caught.message) : showError(caught),
  });
  /**
   * Another time instead of theirs, sent as the answer to this request so a
   * colleague's answer in the meantime is refused rather than moved again.
   * A slot conflict stays on the card with the form, so another time can be
   * tried; a request that moved on is said at the top, as for the others.
   */
  const move = useMutation({
    mutationFn: () =>
      scheduleApi.reschedule(appointment.id, {
        scheduledDate: moveDate,
        startTimeLocal: moveTime,
        expectedChangeRequestedAt: appointment.changeRequestedAt ?? undefined,
      }),
    onSuccess,
    onError: reportRefusal,
  });
  // The booked time is not another time: saving it would change nothing and
  // leave the request open. "Keep the booked time" is that answer.
  const moveUnchanged = moveDate === appointment.scheduledDate && moveTime === appointment.startTimeLocal;
  const busy = accept.isPending || keep.isPending || move.isPending;

  return (
    <article className="card" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={headingId} className="text-lg font-semibold">
            <Link
              to={`/customers/${appointment.customerId}`}
              className="text-[color:var(--color-brand)] hover:underline"
            >
              {appointment.customerName}
            </Link>
          </h2>
          <p className="text-sm">
            {t('changeRequests.bookedFor', { when: booked })} · {formatDuration(appointment.durationMinutes)}
          </p>
          {appointment.propertyAddress ? (
            <p className="text-sm text-[color:var(--color-muted)]">{appointment.propertyAddress}</p>
          ) : null}
        </div>
        {appointment.changeRequestedAt ? (
          <span className="text-sm text-[color:var(--color-muted)]">
            {t('changeRequests.askedOn', { when: formatShortInstant(appointment.changeRequestedAt) })}
          </span>
        ) : null}
      </div>

      {appointment.changeRequestMessage ? (
        <figure className="mt-3 rounded-lg border border-[color:var(--color-line)] p-3">
          <figcaption className="mb-1 text-xs font-semibold text-[color:var(--color-muted)]">
            {t('changeRequests.theyWrote')}
          </figcaption>
          <blockquote className="text-sm">{appointment.changeRequestMessage}</blockquote>
        </figure>
      ) : null}

      {proposed ? (
        <p className="mt-3 text-sm font-semibold">
          {proposedHasPassed
            ? t('changeRequests.requestedTimePassed', { when: proposed })
            : t('changeRequests.requestedTime', { when: proposed })}
        </p>
      ) : null}

      {error ? (
        <div className="mt-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {canAnswer ? (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {/* The customer's own pick is the emphasized answer, when there is
                one still ahead: it is what they asked for. */}
            {proposed && !proposedHasPassed ? (
              <Button
                variant="primary"
                busy={accept.isPending}
                disabled={busy && !accept.isPending}
                onClick={() => accept.mutate()}
              >
                {t('appointmentActions.moveToRequestedTime', { when: proposed })}
              </Button>
            ) : null}
            <Button busy={keep.isPending} disabled={busy && !keep.isPending} onClick={() => keep.mutate()}>
              {t('changeRequests.keep')}
            </Button>
            <Button
              variant="ghost"
              aria-expanded={rescheduling}
              disabled={busy}
              onClick={() => setRescheduling((open) => !open)}
            >
              {t('changeRequests.pickAnother')}
            </Button>
            <Link
              to={`/schedule?appointment=${appointment.id}`}
              className="text-sm text-[color:var(--color-brand)] hover:underline"
            >
              {t('changeRequests.openVisit')}
            </Link>
          </div>
          <p className="mt-2 text-xs text-[color:var(--color-muted)]">{t('changeRequests.keepHint')}</p>

          {rescheduling ? (
            <div className="mt-3 rounded-lg border border-[color:var(--color-line)] p-3">
              {/* The crew's week, with this visit's own hour left out so it
                  does not block its own move. A click lands in the fields. */}
              <AvailabilityPicker
                userIds={appointment.assignedUserIds}
                durationMinutes={appointment.durationMinutes}
                selected={{ date: moveDate, time: moveTime }}
                excludeAppointmentId={appointment.id}
                onPick={(date, time) => {
                  setMoveDate(date);
                  setMoveTime(time);
                }}
              />
              <div className="grid gap-x-3 sm:grid-cols-2">
                <TextInput
                  label={t('appointmentActions.newDate')}
                  type="date"
                  value={moveDate}
                  onChange={(event) => setMoveDate(event.target.value)}
                />
                <TextInput
                  label={t('appointmentActions.newTime')}
                  type="time"
                  step={300}
                  value={moveTime}
                  onChange={(event) => setMoveTime(event.target.value)}
                />
              </div>
              <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('appointmentActions.rescheduleNotice')}</p>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  busy={move.isPending}
                  disabled={moveUnchanged || (busy && !move.isPending)}
                  onClick={() => move.mutate()}
                >
                  {t('appointmentActions.moveVisit')}
                </Button>
                <Button variant="ghost" disabled={move.isPending} onClick={() => setRescheduling(false)}>
                  {t('appointmentActions.keepAsIs')}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </article>
  );
}
